import React from 'react';
import { PackMeta, SmartActionBtn } from './StockPrimitives';

const fmt = (n) => `₹${(+n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const GROUPS = [
  {
    key: 'critical',
    label: 'Critical',
    match: (u) => u === 'expired' || u === 'critical',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-500',
    sub: 'Expired or expiring in 3 days or less',
  },
  {
    key: 'warning',
    label: 'Medium',
    match: (u) => u === 'warning',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    dot: 'bg-orange-400',
    sub: 'Expiring in 3–7 days',
  },
  {
    key: 'normal',
    label: 'Low',
    match: (u) => u === 'normal',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-400',
    sub: 'Expiring in 7–15 days',
  },
];

const StatusBadge = ({ urgency, daysLeft }) => {
  if (urgency === 'expired') return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">Expired {Math.abs(daysLeft)}d ago</span>;
  if (urgency === 'critical') return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">{daysLeft}d left</span>;
  if (urgency === 'warning')  return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">{daysLeft}d left</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">{daysLeft}d left</span>;
};

const ExpiryTab = ({
  expiryItems,
  filtered,
  openActions,
  addressedActions,
  skippedActions,
  onOpenAction,
  batchTrackingEnabled,
  wacMap = {},
}) => {
  if (batchTrackingEnabled === false) {
    return (
      <div className="py-12 text-center px-8 max-w-sm mx-auto">
        <p className="text-muted-foreground text-xs leading-relaxed">
          Batch tracking not active. Record purchases with an{' '}
          <span className="font-semibold">Expiry Date</span> to start tracking expiry risk here.
        </p>
      </div>
    );
  }

  const items = Array.isArray(expiryItems) ? expiryItems : (expiryItems?.products || []);
  const rows = filtered(items, 'product_name');

  const hasAny = GROUPS.some(g => rows.some(r => g.match(r.urgency)));

  if (!hasAny) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">
        No expiry risks in the next 15 days
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {GROUPS.map((group) => {
        const groupRows = rows
          .filter(r => group.match(r.urgency))
          .sort((a, b) => (a.days_until_expiry ?? Infinity) - (b.days_until_expiry ?? Infinity));
        if (!groupRows.length) return null;

        return (
          <div key={group.key}>
            {/* Section header */}
            <div className={`flex items-center gap-2 px-4 py-2.5 ${group.bg} border-y ${group.border}`}>
              <span className={`w-2 h-2 rounded-full ${group.dot}`} />
              <span className={`text-xs font-semibold uppercase tracking-wide ${group.text}`}>{group.label}</span>
              <span className={`text-xs ${group.text} opacity-70`}>— {group.sub}</span>
              <span className={`ml-auto text-xs font-medium ${group.text}`}>{groupRows.length} item{groupRows.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Rows */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wide">
                  <th className="text-left py-2.5 px-4 font-medium">Batch ID</th>
                  <th className="text-left py-2.5 px-4 font-medium">Product</th>
                  <th className="text-right py-2.5 px-4 font-medium">Stock</th>
                  <th className="text-left py-2.5 px-4 font-medium">Expiry Date</th>
                  <th className="text-left py-2.5 px-4 font-medium">Status</th>
                  <th className="text-right py-2.5 px-4 font-medium">Expiry Risk</th>
                  <th className="py-2.5 px-4" />
                </tr>
              </thead>
              <tbody>
                {groupRows.map((item, i) => {
                  const risk = (wacMap[item.catalog_id] || 0) * (item.stock || 0);
                  return (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{item.batch_no ?? '—'}</td>
                      <td className="py-3 px-4">
                        <span className="font-medium">{item.product_name || item.name}</span>
                        <div className="mt-0.5"><PackMeta packSize={item.pack_size} unit={item.unit} /></div>
                      </td>
                      <td className="py-3 px-4 text-right">{item.stock}</td>
                      <td className="py-3 px-4 text-muted-foreground">{item.expiry_date?.slice(0, 10) ?? '—'}</td>
                      <td className="py-3 px-4">
                        <StatusBadge urgency={item.urgency} daysLeft={item.days_until_expiry} />
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {risk > 0 ? <span className="text-red-600">{fmt(risk)}</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <SmartActionBtn
                          catalogId={item.catalog_id}
                          productName={item.product_name || item.name}
                          openActions={openActions}
                          addressedActions={addressedActions}
                          skippedActions={skippedActions}
                          onOpenAction={onOpenAction}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default ExpiryTab;
